import { Dispatch } from 'redux';
import {
  ConversationToMessagesDto,
  Message,
  Acknowledgement,
  PartyMember,
} from '../components/shared/messages/index.d';
import {
  DTO_URL_PREFIXES,
  MESSAGES_TABS_NEW,
  MESSAGE_SELECTED,
  USERS_RELATIVE_URL,
} from '../constants/appConstants';
import { checkAckTime, getRecipientNames } from '../components/shared/messages/message';
import { HasId } from '../types';
import setSidebarSelected from '../store/actions/sidebarActions';
import { setCurrentTab, updateToFieldValue } from '../store/actions/messageActions';
import { DriverDetail } from '../store/selectors/messageSelectors';

export function getNewMessage(
  messages: (ConversationToMessagesDto & HasId)[],
  newMessageObject: Message,
) {
  const allMessages = messages;
  let newMessage = newMessageObject;
  const conversationIndex = allMessages.findIndex(m => {
    newMessage = Array.isArray(newMessage) ? newMessage[0] : newMessage;
    return newMessage && m.conversationsUrl === newMessage.conversation.url;
  });
  if (conversationIndex === -1) {
    // Will add the logic in gtx-1542 after POST response changes
  } else {
    const message = allMessages[conversationIndex];
    const { conversations } = message;
    conversations.latestMessage = newMessage;

    const { items } = conversations;
    const messageIndex = messages.findIndex(m => newMessage && m.id === newMessage.id);
    if (messageIndex === -1) {
      items.push(newMessage);
    } else {
      items[messageIndex] = newMessage;
    }

    conversations.items = items;
    message.conversations = conversations;
    allMessages[conversationIndex] = message;
  }
  return allMessages;
}

export function updateAcknowledgement(
  messages: ConversationToMessagesDto[],
  ackObject: Acknowledgement,
) {
  const conversationIndex = messages.findIndex(
    c => c.conversationsUrl === ackObject.conversation.url,
  );

  if (conversationIndex !== -1) {
    const updatedMessages = [...messages];
    const m = { ...updatedMessages[conversationIndex] };
    const conversations = { ...m.conversations };
    const acks = [...conversations.conversationAcknowledgements];

    let ackIndex = acks.findIndex(a => a.url === ackObject.url);
    // find ack index by party member if not found
    if (ackIndex === -1) {
      ackIndex = acks.findIndex(a => a.partyMember.url === ackObject.partyMember.url);
    }

    if (ackIndex !== -1) {
      acks[ackIndex] = ackObject;
      conversations.conversationAcknowledgements = acks;
      m.conversations = conversations;
      updatedMessages[conversationIndex] = m;
      return updatedMessages;
    }
  }

  return messages;
}

export function generateConversationAcknowledgements(
  newMessage: Message & { tenant: { url: string } },
  regionUrl: string,
): Acknowledgement[] {
  if (newMessage.partiesList) {
    return newMessage.partiesList.map(
      (partyMember): Acknowledgement => ({
        id: -1,
        url: '',
        conversation: { url: newMessage.conversation.url },
        partyMember: { url: partyMember.url },
        lastAcknowledged: '',
      }),
    );
  }
  return [
    {
      id: -1,
      url: '',
      conversation: { url: newMessage.conversation.url },
      partyMember: { url: newMessage.from.url },
      lastAcknowledged: '',
    },
    {
      id: -1,
      url: '',
      conversation: { url: newMessage.conversation.url },
      partyMember: { url: regionUrl },
      lastAcknowledged: '',
    },
  ] as Acknowledgement[];
}

export function updateMessage(
  messages: ConversationToMessagesDto[],
  newMessage: Message & { tenant: { url: string } },
  regionUrl: string,
) {
  const existingThreadIndex = messages.findIndex(
    m => m.conversationsUrl === newMessage.conversation.url,
  );

  if (existingThreadIndex !== -1) {
    const updatedMessages = [...messages];
    const messageDto = { ...updatedMessages[existingThreadIndex] };
    const conversations = { ...messageDto.conversations };

    const includes = newMessage.from.url.includes(USERS_RELATIVE_URL);
    messageDto.containsUnreadMessages = !includes;

    const items = [...conversations.items];
    items.push(newMessage);
    conversations.items = items;
    conversations.latestMessage = newMessage;

    // reset acknowledgement.
    const acks = [...conversations.conversationAcknowledgements];
    const ackIndex = acks.findIndex(a => a.conversation.url === newMessage.conversation.url);
    if (ackIndex !== -1) {
      const ack = { ...acks[ackIndex] };
      ack.lastAcknowledged = '';
      acks[ackIndex] = ack;
    }
    conversations.conversationAcknowledgements = acks;

    messageDto.conversations = conversations;
    updatedMessages[existingThreadIndex] = messageDto;
    return updatedMessages;
  }

  const newMessageToAdd: ConversationToMessagesDto = {
    containsUnreadMessages: !newMessage.from.url.includes(DTO_URL_PREFIXES.REGIONS),
    conversationsUrl: newMessage.conversation.url,
    conversations: {
      commaSeparatedParties: getRecipientNames(newMessage as any),
      items: [newMessage],
      latestMessage: newMessage,
      partiesList: newMessage.partiesList
        ? (newMessage.partiesList as PartyMember[])
        : [
            {
              conversation: '',
              firstName: '',
              fullName: '',
              fullToUrl: '',
              lastName: '',
              url: newMessage.from.url,
            },
            {
              url: regionUrl,
              conversation: '',
              firstName: '',
              fullName: '',
              fullToUrl: '',
              lastName: '',
            },
          ],
      conversationAcknowledgements: generateConversationAcknowledgements(newMessage, regionUrl),
    },
  };
  return messages.concat([newMessageToAdd as any]);
}

export function checkUnread(allMessages: ConversationToMessagesDto[]): boolean {
  try {
    return allMessages.some(m => {
      const convo = m.conversations;
      const latestMessageIdx = convo.items.length - 1;
      const latestMessage = convo.items[latestMessageIdx];
      const acknowledgements = convo.conversationAcknowledgements;
      const ack = acknowledgements.filter(a =>
        a.partyMember.url.includes(DTO_URL_PREFIXES.REGIONS),
      );
      const unread = checkAckTime(latestMessage.sent, ack[0].lastAcknowledged);
      if (latestMessage.from && latestMessage.from.url) {
        return latestMessage.from.url.includes(DTO_URL_PREFIXES.REGIONS) ||
          latestMessage.from.url.includes(USERS_RELATIVE_URL)
          ? false
          : unread;
      }
      return checkAckTime(latestMessage.sent, ack[0].lastAcknowledged);
    });
  } catch (error) {
    return false;
  }
}

export function replaceMessagesInConversation(
  allMessages: ConversationToMessagesDto[],
  messages: ConversationToMessagesDto,
) {
  const index = allMessages.findIndex(m => m.conversationsUrl === messages.conversationsUrl);
  if (index !== -1) {
    const updatedMessages = [...allMessages];
    const existingItems = updatedMessages[index].conversations.items;

    // Check if we are adding a single new message that might already exist
    // This often happens when "replacing" conversation with a response that contains only the new message
    if (messages.conversations.items.length === 1) {
      const newMessage = messages.conversations.items[0];
      const messageExists = existingItems.some(item => item.id === newMessage.id);

      if (!messageExists) {
        updatedMessages[index] = {
          ...updatedMessages[index],
          conversations: {
            ...updatedMessages[index].conversations,
            items: [...existingItems, newMessage],
            latestMessage: newMessage,
          },
        };
        return updatedMessages;
      }
    }

    // Default behavior: replace items if it looks like a full update or we aren't sure
    // But for the specific issue described, likely we want to append if it's just an update
    // However, the function name "replaceMessagesInConversation" implies replacement.
    // If the backend sends a "full" list, we should replace.
    // If the backend sends just the new message (which seems to be the case in some flows), we should append.
    // Let's rely on merging logic: unique items by ID.
    const newItems = messages.conversations.items;
    const combinedItems = [...existingItems];

    newItems.forEach(newItem => {
      if (!combinedItems.some(existing => existing.id === newItem.id)) {
        combinedItems.push(newItem);
      }
    });

    // Sort by sent date just in case
    combinedItems.sort((a, b) => new Date(a.sent).getTime() - new Date(b.sent).getTime());

    updatedMessages[index] = {
      ...updatedMessages[index],
      conversations: {
        ...updatedMessages[index].conversations,
        items: combinedItems,
        latestMessage: combinedItems[combinedItems.length - 1],
      },
    };
    return updatedMessages;
  }
  return allMessages;
}

export function truncateDriver(driver: DriverDetail) {
  return {
    fullName: `${driver.firstName} ${driver.lastName}`,
    firstName: driver.firstName,
    lastName: driver.lastName,
    url: driver.url,
    label: driver.label,
  };
}

export function messageDrivers(drivers: any[]) {
  return (dispatch: Dispatch, getState: any) => {
    if (!drivers) {
      return;
    }

    const distinctDrivers: any[] = [];

    drivers.forEach(driver => {
      if (driver && distinctDrivers.filter(d => d.url === driver?.url).length === 0) {
        distinctDrivers.push(driver);
      }
    });

    dispatch(setSidebarSelected(MESSAGE_SELECTED, true));
    dispatch(setCurrentTab(MESSAGES_TABS_NEW));
    dispatch(updateToFieldValue(distinctDrivers as any));
  };
}
